import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import api from '@/lib/api';
import AtlasNode from '@/components/canvas/AtlasNode';
import CollaboratorAvatars from '@/components/collab/CollaboratorAvatars';
import CollabCursors from '@/components/collab/CollabCursors';
import useCollaboration from '@/hooks/useCollaboration';
import useCanvasStore from '@/store/canvasStore';
import { ArrowLeft } from 'lucide-react';

export default function CollabCanvasPage() {
  const { mapId } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [mapTitle, setMapTitle] = useState('Loading...');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reactFlowWrapper = useRef(null);
  const lastCursorUpdate = useRef(0);
  const pendingUpdate = useRef(null);
  const isReceivingUpdate = useRef(false);
  const initialLoadDone = useRef(false);

  // Initialize canvas store for edit mode
  const loadMap = useCanvasStore(s => s.loadMap);
  const storeNodes = useCanvasStore(s => s.nodes);
  const storeEdges = useCanvasStore(s => s.edges);
  const setMode = useCanvasStore(s => s.setMode);
  const setStoreNodes = useCanvasStore(s => s.setNodes);
  const setStoreEdges = useCanvasStore(s => s.setEdges);
  const updateNodePosition = useCanvasStore(s => s.updateNodePosition);
  const updateNodeLabel = useCanvasStore(s => s.updateNodeLabel);
  const setNodesAndEdges = useCanvasStore(s => s.setNodesAndEdges);

  // Get session from localStorage
  const [session, setSession] = useState(null);
  
  useEffect(() => {
    const stored = localStorage.getItem('collab_session');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.map_id === mapId) {
        setSession(parsed);
      } else {
        setError('Invalid session for this map');
      }
    } else {
      setError('No session found. Please join via share link.');
    }
  }, [mapId]);

  // Collaboration hook
  const {
    participants,
    cursors,
    isConnected,
    remoteNodeDrag,
    remoteTextEdit,
    sendCursorPosition,
    sendNodeDrag,
    sendNodeDragEnd,
    sendTextEdit,
    sendMapUpdate,
  } = useCollaboration(mapId, session?.session_token, false);

  // Load map data and initialize store
  useEffect(() => {
    if (!session) return;
    
    const fetchMapData = async () => {
      try {
        const res = await api.get(`/collab/map/${mapId}`, {
          headers: { 'X-Collab-Session': session.session_token }
        });
        const mapNodes = res.data.nodes || [];
        const mapEdges = res.data.edges || [];
        
        // Initialize canvas store with map data
        loadMap(mapId, res.data.title || 'Untitled Map', mapNodes, mapEdges, {}, {});
        setNodes(mapNodes);
        setEdges(mapEdges);
        setMapTitle(res.data.title || 'Untitled Map');
        
        // Set to edit mode AFTER loading
        setTimeout(() => {
          setMode('edit');
          initialLoadDone.current = true;
        }, 100);
      } catch (err) {
        setError('Failed to load map');
      } finally {
        setLoading(false);
      }
    };
    fetchMapData();
  }, [mapId, session, setNodes, setEdges, loadMap, setMode]);

  // Listen for map updates from other users (via WebSocket)
  useEffect(() => {
    window.__collabMapUpdate = (newNodes, newEdges) => {
      if (newNodes && newEdges && initialLoadDone.current) {
        console.log('Guest received map update from collaborator:', newNodes.length, 'nodes');
        isReceivingUpdate.current = true;
        
        // Update local ReactFlow state
        setNodes(newNodes);
        setEdges(newEdges);
        
        // Update store state for AtlasNode to work
        setNodesAndEdges(newNodes, newEdges);
        
        // Reset flag after a short delay
        setTimeout(() => {
          isReceivingUpdate.current = false;
        }, 100);
      }
    };
    
    // Expose sendTextEdit for components to use
    window.__collabSendTextEdit = sendTextEdit;
    
    return () => {
      window.__collabMapUpdate = null;
      window.__collabSendTextEdit = null;
    };
  }, [setNodes, setEdges, setNodesAndEdges, sendTextEdit]);

  // Apply remote node drag position in real-time
  useEffect(() => {
    if (!remoteNodeDrag) return;
    
    const { nodeId, position } = remoteNodeDrag;
    console.log('CollabCanvas: Applying remote node drag:', nodeId, position);
    // Update local ReactFlow state - always apply, even during load
    setNodes(nds => {
      const updated = nds.map(node => 
        node.id === nodeId 
          ? { ...node, position } 
          : node
      );
      console.log('CollabCanvas: Updated nodes count:', updated.length);
      return updated;
    });
    // Also update store
    updateNodePosition(nodeId, position);
  }, [remoteNodeDrag, setNodes, updateNodePosition]);

  // Apply remote text edit in real-time
  useEffect(() => {
    if (!remoteTextEdit) return;
    
    const { nodeId, label } = remoteTextEdit;
    console.log('CollabCanvas: Applying remote text edit:', nodeId, label);
    // Update local ReactFlow state
    setNodes(nds => nds.map(node => 
      node.id === nodeId 
        ? { ...node, data: { ...node.data, label } } 
        : node
    ));
    // Also update store
    updateNodeLabel(nodeId, label);
  }, [remoteTextEdit, setNodes, updateNodeLabel]);

  // Broadcast local changes to other users (when WE make changes)
  useEffect(() => {
    // Skip if initial load not done or if we're receiving an update from others
    if (!initialLoadDone.current || isReceivingUpdate.current) return;
    if (storeNodes.length === 0) return;
    
    // Sync store to local ReactFlow state
    setNodes(storeNodes);
    setEdges(storeEdges);
    
    // Broadcast changes (debounced) - only for full map syncs, not during drag
    if (pendingUpdate.current) {
      clearTimeout(pendingUpdate.current);
    }
    pendingUpdate.current = setTimeout(() => {
      if (!isReceivingUpdate.current) {
        console.log('Broadcasting local changes:', storeNodes.length, 'nodes');
        sendMapUpdate(storeNodes, storeEdges);
      }
    }, 500); // Increased debounce since we have real-time events now
  }, [storeNodes, storeEdges, setNodes, setEdges, sendMapUpdate]);

  // Handle node drag - broadcast position in real-time
  const handleNodeDrag = useCallback((event, node) => {
    if (!isConnected) return;
    sendNodeDrag(node.id, node.position);
  }, [isConnected, sendNodeDrag]);

  // Handle node drag stop - persist and broadcast final state
  const handleNodeDragStop = useCallback((event, node) => {
    if (!isConnected) return;
    // Update the node position in our nodes array
    const updatedNodes = nodes.map(n => 
      n.id === node.id ? { ...n, position: node.position } : n
    );
    sendNodeDragEnd(updatedNodes, edges);
  }, [isConnected, nodes, edges, sendNodeDragEnd]);

  // Track mouse movement for cursor sharing (throttled)
  const handleMouseMove = useCallback((e) => {
    if (!isConnected) return;
    const now = Date.now();
    if (now - lastCursorUpdate.current < 50) return;
    lastCursorUpdate.current = now;
    sendCursorPosition(e.clientX, e.clientY);
  }, [sendCursorPosition, isConnected]);

  // Node types memo
  const nodeTypes = useMemo(() => ({ atlasNode: AtlasNode }), []);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-cobalt hover:underline"
          >
            Go to homepage
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div 
      className="h-screen w-screen flex flex-col bg-[#FAFAFA]"
      onMouseMove={handleMouseMove}
    >
      {/* Header */}
      <div className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-3 sm:px-4 shrink-0 z-10">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            onClick={() => {
              localStorage.removeItem('collab_session');
              navigate('/');
            }}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div className="min-w-0">
            <h1 className="font-heading text-sm font-medium text-gray-900 truncate">{mapTitle}</h1>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400 truncate">{session?.username}</span>
              <span 
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: session?.cursor_color }}
              />
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Collaborators */}
          <CollaboratorAvatars participants={participants} />
          
          {/* Connection status */}
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
            isConnected ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} />
            <span className="hidden sm:inline">{isConnected ? 'Live' : 'Connecting...'}</span>
          </div>
        </div>
      </div>

      {/* Canvas - Same as main CanvasPage */}
      <div className="flex-1 relative" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDrag={handleNodeDrag}
          onNodeDragStop={handleNodeDragStop}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={{
            type: 'smoothstep',
            style: { strokeWidth: 2, stroke: '#94A3B8' }
          }}
        >
          <Background color="#E2E8F0" gap={24} size={1} />
          <Controls position="bottom-left" showInteractive={false} />
          <MiniMap 
            position="bottom-right"
            nodeColor="#CBD5E1"
            maskColor="rgba(0,0,0,0.08)"
            className="hidden sm:block"
          />
        </ReactFlow>

        {/* Remote cursors overlay */}
        <CollabCursors cursors={cursors} />
      </div>
    </div>
  );
}
